import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';

interface AdminUser {
  email: string;
  role: string;
}

interface ContactMessage {
  name: string;
  email: string;
  message: string;
  createdAt: string;
  metadata?: {
    source?: string;
    ip?: string;
    userAgent?: string;
  };
}

interface MessagesApiResponse {
  success?: boolean;
  messages?: ContactMessage[];
  error?: string;
}

type NavItem = {
  key: string;
  label: string;
  to?: string;
};

const navItems: NavItem[] = [
  { key: 'dashboard', label: '📊 Dashboard', to: '/admin/dashboard' },
  { key: 'users', label: '👥 Users' },
  { key: 'content', label: '📝 Content' },
  { key: 'messages', label: '📧 Messages' },
  { key: 'settings', label: '⚙️ Settings' },
];

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [activeNav, setActiveNav] = useState(navItems[0].key);
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is logged in
    const isAdmin = localStorage.getItem('isAdmin');
    const adminUser = localStorage.getItem('adminUser');

    if (!isAdmin || !adminUser) {
      navigate('/admin');
      return;
    }

    setUser(JSON.parse(adminUser));
  }, [navigate]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const controller = new AbortController();

    const loadMessages = async () => {
      setMessagesLoading(true);
      setMessagesError(null);
      setMessages([]);
      try {
        const response = await fetch('/api/load_messages', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });
        const payload = (await response.json()) as MessagesApiResponse;
        if (!response.ok) {
          throw new Error(payload?.error ?? 'Failed to load messages');
        }
        setMessages(Array.isArray(payload?.messages) ? payload.messages : []);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        const errorMessage = error instanceof Error ? error.message : 'Unable to load messages';
        setMessagesError(errorMessage);
      } finally {
        setMessagesLoading(false);
      }
    };

    void loadMessages();

    return () => {
      controller.abort();
    };
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminUser');
    setMessages([]);
    setMessagesLoading(true);
    setMessagesError(null);
    navigate('/admin');
  };

  const handleNavClick = (item: NavItem) => {
    setActiveNav(item.key);
    if (item.to) {
      navigate(item.to);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div style={styles.loading}>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <div style={styles.logo}>
            <h2 style={styles.logoText}>🎓 Admin Panel</h2>
          </div>
          <nav style={styles.nav}>
            {navItems.map((item) => {
              const isActive = activeNav === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleNavClick(item)}
                  style={{
                    ...styles.navItem,
                    ...(isActive ? styles.navItemActive : {}),
                  }}
                  aria-pressed={isActive}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            🚪 Logout
          </button>
        </aside>

        {/* Main Content */}
        <main style={styles.main}>
          {/* Header */}
          <header style={styles.header}>
            <h1 style={styles.pageTitle}>Dashboard</h1>
            <div style={styles.userInfo}>
              <span>Welcome, {user.email}</span>
            </div>
          </header>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>👥</div>
              <div style={styles.statInfo}>
                <h3 style={styles.statNumber}>1,234</h3>
                <p style={styles.statLabel}>Total Users</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>📝</div>
              <div style={styles.statInfo}>
                <h3 style={styles.statNumber}>56</h3>
                <p style={styles.statLabel}>Active Courses</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>📧</div>
              <div style={styles.statInfo}>
                <h3 style={styles.statNumber}>23</h3>
                <p style={styles.statLabel}>New Messages</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>💰</div>
              <div style={styles.statInfo}>
                <h3 style={styles.statNumber}>$12,450</h3>
                <p style={styles.statLabel}>Revenue</p>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Recent Activity</h2>
            <div style={styles.activityList}>
              {messagesLoading ? (
                <p style={styles.sectionMessage}>Loading latest messages…</p>
              ) : messagesError ? (
                <p style={styles.sectionMessage}>Failed to load messages: {messagesError}</p>
              ) : messages.length === 0 ? (
                <p style={styles.sectionMessage}>No messages received yet.</p>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={`${message.email}-${message.createdAt}-${index}`}
                    style={styles.activityItem}
                  >
                    <span style={styles.activityDot}></span>
                    <div style={styles.activityContent}>
                      <div style={styles.messageHeader}>
                        <span style={styles.messageName}>{message.name}</span>
                        <span style={styles.activityTime}>{formatRelativeTime(message.createdAt)}</span>
                      </div>
                      <p style={styles.activityText}>{truncateText(message.message, 160)}</p>
                      <div style={styles.messageMeta}>
                        <span style={styles.messageEmail}>{message.email}</span>
                        {message.metadata?.source ? (
                          <span style={styles.messageSource}>{message.metadata.source}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '80vh',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    fontSize: '18px',
    color: '#1e90ff',
  },
  sidebar: {
    width: '250px',
    backgroundColor: '#1e90ff',
    color: '#fff',
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column',
  },
  logo: {
    padding: '0 20px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    marginBottom: '20px',
  },
  logoText: {
    margin: 0,
    fontSize: '18px',
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  navItem: {
    padding: '12px 20px',
    color: 'rgba(255,255,255,0.8)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '15px',
  },
  navItemActive: {
    backgroundColor: '#ff8c00',
    color: '#fff',
  },
  logoutBtn: {
    margin: '20px',
    padding: '12px',
    backgroundColor: '#ff8c00',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
  },
  main: {
    flex: 1,
    padding: '30px',
    backgroundColor: '#f8f9fa',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  pageTitle: {
    fontSize: '28px',
    color: '#1e90ff',
    margin: 0,
  },
  userInfo: {
    color: '#666',
    fontSize: '14px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderLeft: '4px solid #ff8c00',
  },
  statIcon: {
    fontSize: '40px',
  },
  statInfo: {
    flex: 1,
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1e90ff',
    margin: 0,
  },
  statLabel: {
    fontSize: '14px',
    color: '#888',
    margin: 0,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#1e90ff',
    marginTop: 0,
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #ff8c00',
  },
  sectionMessage: {
    margin: 0,
    color: '#555',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '6px',
  },
  messageName: {
    fontWeight: 600,
    color: '#1e90ff',
  },
  messageMeta: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    fontSize: '12px',
    color: '#777',
    marginTop: '6px',
  },
  messageEmail: {
    fontStyle: 'italic',
  },
  messageSource: {
    backgroundColor: '#e6f2ff',
    color: '#0050a0',
    borderRadius: '12px',
    padding: '2px 8px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  activityDot: {
    width: '10px',
    height: '10px',
    backgroundColor: '#ff8c00',
    borderRadius: '50%',
    marginTop: '5px',
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    margin: 0,
    color: '#333',
    fontSize: '14px',
  },
  activityTime: {
    fontSize: '12px',
    color: '#999',
  },
};

export default Dashboard;

const truncateText = (value: string, limit: number): string => {
  const trimmed = value.trim();
  if (trimmed.length <= limit) {
    return trimmed;
  }
  return `${trimmed.slice(0, limit).trim()}…`;
};

const formatRelativeTime = (timestamp?: string): string => {
  if (!timestamp) {
    return 'Unknown time';
  }
  const createdAt = new Date(timestamp);
  if (!Number.isFinite(createdAt.getTime())) {
    return 'Unknown time';
  }
  const diffMs = Date.now() - createdAt.getTime();
  if (diffMs < 60000) {
    const seconds = Math.max(1, Math.floor(diffMs / 1000));
    return `${seconds} seconds ago`;
  }
  if (diffMs < 3600000) {
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    return `${minutes} minutes ago`;
  }
  if (diffMs < 86400000) {
    const hours = Math.max(1, Math.floor(diffMs / 3600000));
    return `${hours} hours ago`;
  }
  return createdAt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
